/**
 * SplitEditor (PHASE1 §7.2). A modal for splitting one transaction across
 * several buckets. Confirm is gated on integer-cents arithmetic — the summed
 * lines must equal `parseAmountToCents(txn.amount)` exactly AND every line must have a
 * bucket — which mirrors the backend `split_sum_mismatch` invariant. It emits
 * `SplitInput[]` with canonical decimal-string amounts.
 *
 * The editable form lives in a child that only mounts while the modal is open
 * (Radix unmounts closed content), so it resets cleanly on every open via a
 * `useState` initializer — no reset effect needed.
 */
import { useMemo, useRef, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Modal } from '../../components/ui/Modal.tsx';
import { Button } from '../../components/ui/Button.tsx';
import { Input } from '../../components/ui/Input.tsx';
import { BucketSelect } from '../../components/ui/BucketSelect.tsx';
import { formatAmountInput, money, parseAmountToCents } from '../../lib/format.ts';
import type { InboxItem, SplitInput } from '../../lib/types.ts';

interface SplitLine {
  id: number;
  bucketId: number | null;
  amount: string;
}

/** Render signed integer cents back to a canonical `"-49.90"` decimal string. */
function centsToMoney(cents: number): string {
  const sign = cents < 0 ? '-' : '';
  const abs = Math.abs(cents);
  return `${sign}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
}

export interface SplitEditorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  transaction: InboxItem;
  onConfirm: (splits: SplitInput[]) => void;
}

export function SplitEditor({ open, onOpenChange, transaction, onConfirm }: SplitEditorProps) {
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Buchung aufteilen"
      description={`Gesamtbetrag ${money(transaction.amount)}`}
      size="lg"
    >
      <SplitForm
        transaction={transaction}
        onConfirm={(splits) => {
          onConfirm(splits);
          onOpenChange(false);
        }}
        onCancel={() => onOpenChange(false)}
      />
    </Modal>
  );
}

interface SplitFormProps {
  transaction: InboxItem;
  onConfirm: (splits: SplitInput[]) => void;
  onCancel: () => void;
}

function SplitForm({ transaction, onConfirm, onCancel }: SplitFormProps) {
  const targetCents = parseAmountToCents(transaction.amount) ?? 0;
  const nextIdRef = useRef(2);
  const [lines, setLines] = useState<SplitLine[]>(() => [
    {
      id: 0,
      bucketId: transaction.suggestion?.bucket_id ?? null,
      // Prefill in the de-DE editing format ("-49,90") the inputs advertise.
      amount: formatAmountInput(transaction.amount),
    },
    { id: 1, bucketId: null, amount: '' },
  ]);

  const sumCents = useMemo(
    () => lines.reduce((total, line) => total + (parseAmountToCents(line.amount) ?? 0), 0),
    [lines],
  );
  const remainderCents = targetCents - sumCents;

  const allParse = lines.every((line) => parseAmountToCents(line.amount) !== null);
  const allBuckets = lines.every((line) => line.bucketId !== null);
  const canConfirm = lines.length > 0 && allParse && allBuckets && remainderCents === 0;

  function updateLine(id: number, patch: Partial<SplitLine>) {
    setLines((prev) => prev.map((line) => (line.id === id ? { ...line, ...patch } : line)));
  }
  function addLine() {
    setLines((prev) => [
      ...prev,
      {
        id: nextIdRef.current++,
        bucketId: null,
        amount: remainderCents !== 0 ? formatAmountInput(centsToMoney(remainderCents)) : '',
      },
    ]);
  }
  function removeLine(id: number) {
    setLines((prev) => prev.filter((line) => line.id !== id));
  }

  function handleConfirm() {
    if (!canConfirm) return;
    const splits: SplitInput[] = lines.map((line) => ({
      bucket_id: line.bucketId as number,
      amount: centsToMoney(parseAmountToCents(line.amount) as number),
    }));
    onConfirm(splits);
  }

  return (
    <div className="space-y-3">
      {lines.map((line, index) => {
        const invalid = line.amount.trim() !== '' && parseAmountToCents(line.amount) === null;
        return (
          <div key={line.id} className="flex items-end gap-2">
            <div className="min-w-0 flex-1">
              <BucketSelect
                value={line.bucketId}
                onChange={(bucketId) => updateLine(line.id, { bucketId })}
              />
            </div>
            <div className="w-28 shrink-0">
              <Input
                type="text"
                inputMode="decimal"
                placeholder="0,00"
                aria-label={`Betrag Zeile ${index + 1}`}
                value={line.amount}
                invalid={invalid}
                className="text-right tabular-nums"
                onChange={(e) => updateLine(line.id, { amount: e.target.value })}
              />
            </div>
            <Button
              variant="ghost"
              size="sm"
              aria-label={`Zeile ${index + 1} entfernen`}
              disabled={lines.length <= 1}
              onClick={() => removeLine(line.id)}
            >
              <Trash2 className="h-4 w-4" aria-hidden />
            </Button>
          </div>
        );
      })}

      <Button variant="ghost" size="sm" onClick={addLine}>
        <Plus className="h-4 w-4" aria-hidden />
        Zeile hinzufügen
      </Button>

      <div
        className={`flex items-center justify-between rounded-lg px-3 py-2 text-sm ${
          remainderCents === 0
            ? 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200'
            : 'bg-amber-50 text-amber-800 dark:bg-amber-950 dark:text-amber-200'
        }`}
      >
        <span className="font-medium">Summe</span>
        <span className="font-medium tabular-nums">
          {money(centsToMoney(sumCents))} / {money(transaction.amount)}
        </span>
      </div>
      {remainderCents !== 0 ? (
        <p className="text-xs text-amber-600 tabular-nums dark:text-amber-400">
          Noch {money(centsToMoney(remainderCents))} zu verteilen.
        </p>
      ) : null}

      <div className="flex items-center justify-end gap-2 pt-2">
        <Button variant="secondary" onClick={onCancel}>
          Abbrechen
        </Button>
        <Button onClick={handleConfirm} disabled={!canConfirm}>
          Übernehmen
        </Button>
      </div>
    </div>
  );
}
